/* Unified Statistics — AI Statistics and CRM stats on one page (design:
   "Page redesign: compact layout" → Statistics, layout 1c + the 1a revenue widget).

   Top to bottom: the header (range, account, Gross/Net), then the tabs —
   Overview · Team · AI detail · Accounts. Overview is the Revenue widget (Gross and
   Net both, split by type or by Seller) over the Seller ledger next to the daily
   chart. The same component is
   the fleet page (no accountId) and an account's Stats → Statistics sub-tab
   (accountId; no Accounts tab — a leaderboard of one says nothing).

   Team needs CRM access (CONTEXT.md "Stats access"): without it the Team tab is
   hidden and the Team share reads "No CRM data". Every number covers only the
   accounts the user owns or has Stats access on — the backend scopes it. */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { C, FONT } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Icon } from '../../ChatAssistant/compact/kit';
import { useCrmHost } from '../../../crm/CrmHostContext';
import { AccountsTable } from './AccountsTable';
import { AiDetail } from './AiDetail';
import { DailyChart } from './DailyChart';
import { Header, type PickerAccount } from './Header';
import { RevenueWidget, type SplitAxis } from './RevenueWidget';
import { SellerLedger } from './SellerLedger';
import { TeamTable } from './TeamTable';
import { useStatisticsData } from './useStatisticsData';
import { daysBetween, kyivToday, shiftDays, type MoneyMode } from './model';
import './unified.css';

type Tab = 'overview' | 'team' | 'ai' | 'accounts';

interface Props {
  /** Fix the page to one account (the account's Stats tab). */
  accountId?: string;
  /** Account surface only: jump to a Chat assistant sub-tab from a zero state. */
  onOpenSubTab?: (id: string) => void;
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: `1px solid ${C.tintLine}`, borderRadius: 8, background: 'color-mix(in srgb, var(--om-accent) 6%, transparent)', font: f(400, 12.5), color: C.purplePale }}>
      <Icon name="alert-circle" size={14} />{children}
    </div>
  );
}

export function UnifiedStatistics({ accountId: fixedAccount, onOpenSubTab }: Props) {
  const { t } = useTranslation('statsUnified');
  const crm = useCrmHost();
  const hasCrm = crm.access.state === 'ok';
  const [params, setParams] = useSearchParams();
  const today = useMemo(() => kyivToday(), []);

  const [preset, setPreset] = useState<number | null>(7);
  const [start, setStart] = useState(() => shiftDays(today, -6));
  const [end, setEnd] = useState(today);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [axis, setAxis] = useState<SplitAxis>('type');
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const accountId = fixedAccount ?? picked;
  const accountScoped = !!accountId;

  const mode: MoneyMode = params.get('money') === 'net' ? 'net' : 'gross';
  const setMode = (m: MoneyMode) => {
    const next = new URLSearchParams(params);
    if (m === 'net') next.set('money', 'net'); else next.delete('money');
    setParams(next, { replace: true });
  };
  const rawTab = params.get('view');
  const tabs: Tab[] = ['overview', ...(hasCrm ? ['team' as Tab] : []), 'ai', ...(fixedAccount ? [] : ['accounts' as Tab])];
  const tab: Tab = tabs.includes(rawTab as Tab) ? (rawTab as Tab) : 'overview';
  const setTab = (v: Tab) => {
    const next = new URLSearchParams(params);
    if (v === 'overview') next.delete('view'); else next.set('view', v);
    setParams(next, { replace: true });
  };

  const data = useStatisticsData(start, end, accountId, crm.access.state !== 'loading');
  const { stats, chat } = data;

  // The fleet's account list for the picker — kept from the last fleet-wide load so
  // narrowing to one account does not empty the picker.
  const [fleet, setFleet] = useState<PickerAccount[]>([]);
  useEffect(() => {
    if (fixedAccount || picked || !stats) return;
    const names = new Map<string, string>();
    stats.money.accounts.forEach((a) => names.set(a.account_id, a.name));
    chat?.accounts.forEach((a) => { if (!names.has(a.account_id)) names.set(a.account_id, a.name); });
    stats.team_accounts.forEach((a) => { if (!names.has(a.account_id)) names.set(a.account_id, a.account_id); });
    setFleet([...names].map(([account_id, name]) => ({ account_id, name })).sort((a, b) => a.name.localeCompare(b.name)));
  }, [stats, chat, fixedAccount, picked]);

  const applyPreset = (days: number) => {
    setPreset(days);
    setEnd(today);
    setStart(shiftDays(today, -(days - 1)));
  };
  const applyRange = (s: string, e: string) => {
    const days = daysBetween(s, e);
    setStart(s);
    setEnd(e);
    setPreset(e === today && [1, 7, 30, 90].includes(days) ? days : null);
  };

  const subtitle = accountScoped ? t('subtitleAccount') : t('subtitle');
  const quiet = !!stats && !!chat && stats.money.revenue.gross === 0 && (chat.totals?.chats_activated ?? 0) === 0 && stats.team.length === 0;

  return (
    <div className="cpt-root stx-root" style={{ minHeight: '100%', background: C.page, color: C.text, fontFamily: FONT, borderRadius: 12 }}>
      <div className="stx-shell" style={{ width: '100%', padding: fixedAccount ? '12px 0 48px' : '20px 24px 96px', display: 'flex', flexDirection: 'column', gap: 14, boxSizing: 'border-box' }}>
        <Header
          subtitle={subtitle}
          preset={preset}
          onPreset={applyPreset}
          start={start}
          end={end}
          today={today}
          onRange={applyRange}
          accounts={fixedAccount ? undefined : fleet}
          accountId={picked}
          onAccount={fixedAccount ? undefined : setPicked}
          mode={mode}
          onMode={setMode}
          loading={data.loading}
          onRefresh={data.reload}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
          <div className="cpt-noscrollbar" style={{ display: 'flex', overflowX: 'auto' }}>
            {tabs.map((id) => {
              const on = id === tab;
              const icon = id === 'overview' ? 'bar-chart-2' : id === 'ai' ? 'sparkles' : id === 'team' ? 'users' : 'list';
              return (
                <button key={id} type="button" onClick={() => setTab(id)} className="cpt-hover-text" style={{ height: 38, padding: '0 12px', marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6, border: 0, borderBottom: `2px solid ${on ? C.purple : 'transparent'}`, background: 'transparent', color: on ? C.text : C.sub, font: f(600, 13), cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <Icon name={icon} size={14} />{t(`tabs.${id}`)}
                </button>
              );
            })}
          </div>
          <span style={{ flex: 1 }} />
          <div ref={setSlot} style={{ display: 'flex', padding: '4px 0' }} />
        </div>

        {data.statsError && <Notice>{t('errors.money')}</Notice>}
        {data.chatError && <Notice>{t('errors.ai')}</Notice>}
        {crm.access.state === 'error' && <Notice>{t('errors.crm')}</Notice>}
        {quiet && !data.loading && <div style={{ padding: '10px 2px', font: f(400, 12.5), color: C.muted }}>{t('quiet')}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: data.loading && (stats || chat) ? 0.6 : 1, transition: 'opacity .15s' }}>
          {tab === 'overview' && (
            <>
              <RevenueWidget money={stats?.money ?? null} mode={mode} days={daysBetween(start, end)} axis={axis} onAxis={setAxis} hasCrm={hasCrm} onOpenTab={setTab} />

              <div className="stx-pair" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: 12, alignItems: 'stretch' }}>
                <SellerLedger stats={stats} chat={chat} mode={mode} hasCrm={hasCrm} />
                <DailyChart stats={stats} chat={chat} mode={mode} hasCrm={hasCrm} />
              </div>
            </>
          )}
          {tab === 'ai' && <AiDetail stats={stats} chat={chat} mode={mode} onOpenSubTab={onOpenSubTab} />}
          {tab === 'team' && hasCrm && <TeamTable stats={stats} chat={chat} mode={mode} accountScoped={accountScoped} toolbarSlot={slot} />}
          {tab === 'accounts' && !fixedAccount && <AccountsTable stats={stats} chat={chat} mode={mode} hasCrm={hasCrm} toolbarSlot={slot} />}
        </div>

        <span style={{ font: f(400, 11), color: C.muted }}>{t('footnote')}</span>
      </div>
    </div>
  );
}
